import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import SBINModal from "./SBINModal";
import { getGiftCardDetails } from "../../api/giftCards";
import { formatShortDate } from "../../utils/formatShortDate";

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "$0.00";
  return `$${num.toFixed(2)}`;
}

export default function GiftCardDetailsModal({ isOpen, code, onClose }) {
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !code) return;
    let isMounted = true;
    setLoading(true);
    setError("");
    getGiftCardDetails(code)
      .then((data) => {
        if (!isMounted) return;
        setDetails(data);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err?.message || "Unable to load gift card details.");
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [isOpen, code]);

  const uses = useMemo(() => details?.uses || [], [details]);
  const isUsed = useMemo(() => uses.length > 0, [uses]);
  const notFoundMessage =
    details?.is_valid === false ? details?.message || "Not found" : "";

  const headerTitle = code ? `Gift Card ${code}` : "Gift Card";
  const remainingText = formatMoney(details?.remaining);
  const totalText = formatMoney(details?.total_amount);
  const purchasedDate = formatShortDate(details?.purchased_at);
  const purchasedBy = details?.purchased_by_name || "";
  const ownerName = details?.owner_name || "";

  const footer = (
    <div className="flex justify-end">
      <button
        type="button"
        onClick={onClose}
        className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400"
      >
        OK
      </button>
    </div>
  );

  return (
    <SBINModal
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle}
      footer={footer}
      fitContent
    >
      {loading && <div className="text-sm text-slate-500">Loading...</div>}
      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
      {!loading && !error && notFoundMessage && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {notFoundMessage}
        </div>
      )}
      {!loading && !error && details && !notFoundMessage && (
        <div className="space-y-4">
          <div className="text-3xl font-semibold text-emerald-600">
            {isUsed ? (
              <span>
                {remainingText}{" "}
                <span className="text-base font-medium text-slate-600">
                  remaining of {totalText}
                </span>
              </span>
            ) : (
              <span>{totalText}</span>
            )}
          </div>
          {ownerName && (
            <div className="text-sm text-slate-700">
              <span className="font-semibold">Owner</span> {ownerName}
            </div>
          )}
          <div className="text-sm text-slate-700">
            <span className="font-semibold">Purchased</span>{" "}
            {purchasedDate || "--"}{" "}
            {purchasedBy ? (
              <>
                <span className="font-semibold">by</span> {purchasedBy}
              </>
            ) : null}
          </div>

          {isUsed ? (
            <div className="overflow-hidden rounded-xl border border-sky-200">
              <div className="grid grid-cols-4 gap-3 bg-sky-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-sky-700">
                <span>Ticket</span>
                <span>Price</span>
                <span>Amount Used</span>
                <span>Date</span>
              </div>
              <div className="divide-y divide-sky-100">
                {uses.map((entry) => (
                  <div
                    key={`${entry.ticket_id}-${entry.used_at}`}
                    className="grid grid-cols-4 gap-3 px-4 py-2 text-sm text-slate-700"
                  >
                    <button
                      type="button"
                      onClick={() => navigate(`/tickets/${entry.ticket_id}`)}
                      className="text-sky-700 underline text-left"
                    >
                      {entry.ticket_number || `#${entry.ticket_id}`}
                    </button>
                    <span>{formatMoney(entry.price)}</span>
                    <span>{formatMoney(entry.amount_used)}</span>
                    <span>{formatShortDate(entry.used_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-sm font-semibold text-slate-700">
              This Gift Card has not been used yet.
            </div>
          )}
        </div>
      )}
    </SBINModal>
  );
}

GiftCardDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  code: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

GiftCardDetailsModal.defaultProps = {
  code: null,
};
